<template>
  <div class="side-nav-outer-toolbar">
    <header-toolbar
      class="layout-header"
      :menu-toggle-enabled="true"
      :toggle-menu-func="toggleMenu"
      :title="title"
    />

      <dx-drawer
      class="layout-body"
      position="before"
      template="menuTemplate"
      v-model:opened="menuOpened"
      :opened-state-mode="drawerOptions.menuMode"
      :reveal-mode="drawerOptions.menuRevealMode"
      :min-size="drawerOptions.minMenuSize"
      :max-size="drawerOptions.maxMenuSize"
      :shading="drawerOptions.shaderEnabled"
      :close-on-outside-click="drawerOptions.closeOnOutsideClick"
      >
      <dx-scroll-view ref="scrollViewRef" class="with-footer">
        <slot />
        <slot name="footer" />
      </dx-scroll-view>
      <template #menuTemplate>
        <side-nav-menu
          ref="sideNavMenuRef"
          :compact-mode="!menuOpened"
          @click="handleSideBarClick"
        />
      </template>
    </dx-drawer>
  </div>
</template>

<script>
import DxDrawer from "devextreme-vue/drawer";
import DxScrollView from "devextreme-vue/scroll-view";

import staticMenuItems from "../app-navigation";
import HeaderToolbar from "../components/header-toolbar";
import SideNavMenu from "../components/side-nav-menu";
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

export default {
  props: {
    title: String,
    isXSmall: Boolean,
    isLarge: Boolean
  },
  setup(props) {
    const route = useRoute();
    const store = useStore();

    const scrollViewRef = ref(null);
    const sideNavMenuRef = ref(null);
    const menuOpened = ref(props.isLarge);
    const menuTemporaryOpened = ref(false);

    function toggleMenu(e) {
      const pointerEvent = e.event;
      pointerEvent.stopPropagation();
      if (menuOpened.value) {
        menuTemporaryOpened.value = false;
      }
      menuOpened.value = !menuOpened.value;
    }

    function handleSideBarClick() {
      if (menuOpened.value === false) {
        menuTemporaryOpened.value = true;
      }
      menuOpened.value = true;
    }

    const drawerOptions = computed(() => {
      const shaderEnabled = !props.isLarge;

      return {
        menuMode: props.isLarge ? "shrink" : "overlap",
        menuRevealMode: props.isXSmall ? "slide" : "expand",
        minMenuSize: props.isXSmall ? 0 : 60,
        maxMenuSize: props.isXSmall ? 250 : undefined,
        closeOnOutsideClick: shaderEnabled,
        shaderEnabled
      };
    });

    // Get live navigation items from Vuex store (with fallback to static config)
    const currentNavItems = computed(() => {
      return store.getters.appNavigation || staticMenuItems;
    });

    // Helper to find the menu item title matching the current route path
    const getTitleForPath = (items, path) => {
      if (!items) return null;
      for (const item of items) {
        if (item.path === path) return item.text;
        if (item.items) {
          const match = getTitleForPath(item.items, path);
          if (match) return match;
        }
      }
      return null;
    };

    // Force-highlight the active menu item via direct DOM manipulation using strict text equality
    const forceHighlightActiveRoute = () => {
      nextTick(() => {
        setTimeout(() => {
          const menuEl = sideNavMenuRef.value?.$el || sideNavMenuRef.value;
          if (!menuEl) return;

          const currentTitle = getTitleForPath(currentNavItems.value, route.path);

          // Clear previous highlights
          const allNodes = menuEl.querySelectorAll('.dx-treeview-node');
          allNodes.forEach(node => {
            node.classList.remove('layout-forced-active');
            const item = node.querySelector('.dx-treeview-item');
            if (item) item.classList.remove('layout-forced-active-item');
          });

          if (!currentTitle) return;

          // Apply persistent highlight class strictly matching the exact text
          allNodes.forEach(node => {
            const itemContent = node.querySelector('.dx-treeview-item');
            if (itemContent && itemContent.textContent.trim() === currentTitle.trim()) {
              node.classList.add('layout-forced-active');
              itemContent.classList.add('layout-forced-active-item');
            }
          });
        }, 120);
      });
    };

    watch(
      () => props.isLarge,
      () => {
        if (!menuTemporaryOpened.value) {
          menuOpened.value = props.isLarge;
        }
    });

    watch(
      () => route.path,
      () => {
        if (menuTemporaryOpened.value || !props.isLarge) {
          menuOpened.value = false;
          menuTemporaryOpened.value = false;
        }
        if (scrollViewRef.value?.instance) {
          scrollViewRef.value.instance.scrollTo(0);
        }
        forceHighlightActiveRoute();
      }
    );

    // Watch store navigation items in case dynamic elements load slightly after initial mount
    watch(
      () => store.getters.appNavigation,
      () => {
        forceHighlightActiveRoute();
      },
      { deep: true }
    );

    onMounted(() => {
      forceHighlightActiveRoute();
    });

    return {
      menuOpened,
      menuItems: currentNavItems,
      toggleMenu,
      handleSideBarClick,
      drawerOptions,
      scrollViewRef,
      sideNavMenuRef
    };
  },
  components: {
    DxDrawer,
    DxScrollView,
    HeaderToolbar,
    SideNavMenu
  }
};
</script>

<style lang="scss">
.dx-swatch-additional .dx-treeview-item {
  // padding: 0 6px;
}

.side-navigation-menu .menu-container .dx-treeview .dx-treeview-node[aria-level="2"] .dx-treeview-item-content  {
  padding: 0 20px;
}
.side-nav-outer-toolbar {
  flex-direction: column;
  display: flex;
  height: 100%;
  width: 100%;
}

.layout-header {
  z-index: 1501;
}

/* Persistent custom highlight styles that stay active even when clicking out into the main content */
.side-navigation-menu, .dx-treeview {
  .layout-forced-active > .dx-treeview-item,
  .layout-forced-active-item {
    background-color: rgba(3, 169, 244, 0.15) !important;
    color: #03a9f4 !important;
    font-weight: 600 !important;
  }
}
</style>